import { AppService } from '@app/app.service';
import { Component, Input, DoCheck, OnInit, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { GridService } from './grid.service';
import { GridsterConfig } from 'angular-gridster2';
import { Router } from '@angular/router';
import * as R from 'ramda';
import Publication from '@app/publication';
import Section from '@app/section';
import settings from '@app/app.settings';

@Component({
  selector: 'app-edit[class="page-content"]',
  templateUrl: './edit.component.html',
  styleUrls: ['../app.component.less', './edit.component.less'],
})
export class EditComponent implements DoCheck, OnInit {
  @Input() activeSection: Section;
  @Input() activeSections: {sectionId?: number, subsectionId?: number} = {};
  @Input() activeSubsection: Section;

  // Single object representing settings from all subsections of all panels
  @Input() publication: Publication = {
    sections: [],
    title   : '',
    layout  : {type: settings.defaultGridLayout},
  };

  @ViewChild('gridLayout') public gridLayoutRef: ElementRef;
  emptyGridLayout: {y: number, x: number}[] = [];
  gridRows = 5;
  gridCols = 5;

  mapDisplayToTilesDimensions = {
    '1280': {width: 124, height: 124 * 1.5, margin: 16},
    '1464': {width: 164, height: 164 * 1.5, margin: 22},
    '1680': {width: 208, height: 208 * 1.5, margin: 26},
    '1920': {width: 240, height: 240 * 1.5, margin: 24},
    '2560': {width: 300, height: 300 * 1.5, margin: 38},
  };

  options: GridsterConfig = {
    draggable: {enabled: true, ignoreContentClass: 'tooltip'},
    minCols        : this.gridCols,
    minRows        : this.gridRows,
    maxCols        : this.gridCols,
    maxRows        : this.gridRows,
    gridType       : 'fixed',
    pushResizeItems: true,
    displayGrid    : 'none',
  };

  constructor(
    private appService: AppService,
    public gridService: GridService,
    private router: Router,
  ) {
    this.gridService.setOptions(this.options);

    for (let i = 0; i < this.gridRows; i++) {
      for (let j = 0; j < this.gridCols; j++) {
        this.emptyGridLayout.push({ x: j, y: i });
      }
    }
  }

  ngDoCheck() {
    this.activeSection = R.find(R.propEq('id', this.activeSections.sectionId), this.publication.sections);

    if (!this.activeSection.layout) {
      this.activeSection.layout = {type: 'wall'};
    }

    if (this.activeSections.subsectionId) {
      this.activeSubsection = R.find(
        R.propEq('id', this.activeSections.subsectionId),
        this.activeSection.subsections || [],
      );
    }

    if (this.activeSubsection && !this.activeSubsection.layout) {
      this.activeSubsection.layout = {type: 'wall'};
    }

    this.gridService.currentLayout = (this.activeSubsection || this.activeSection).layout.type;
  }

  ngOnInit() {
    this.appService
      .getPublication({id: this.router.routerState.snapshot.root.firstChild.firstChild.params.id})

      .subscribe((data: Publication) => {
        const
          collectAllCards = R.compose(
            R.filter(Boolean),
            R.flatten,

            R.map(section => R.compose(
              R.append(R.has('subsections', section) && collectAllCards(section.subsections)),
              R.pathOr([], ['sources']),
            )(section)),
          ),

          allCards = collectAllCards(data.sections);

        this.publication = data;

        if (R.isEmpty(this.publication.sections)) {
          this.publication.sections = [{id: 1, sources: [], title: 'Untitled 1'}];
        }

        const
          firstSection = this.publication.sections[0];

        this.activeSections.sectionId = firstSection.id;

        if (!R.isEmpty(firstSection.subsections)) {
          this.activeSections.subsectionId = firstSection.subsections[0].id;
          this.gridService.setCentralCards(firstSection.subsections[0].sources);
        } else {
          this.gridService.setCentralCards(firstSection.sources);
        }

        this.gridService.filterAndSetPanelCards(R.clone(allCards));
        this.gridService.filterAndSetInitialPanelCards(R.clone(allCards));
      });

    this.windowResizeHandler();
    window.onresize = this.windowResizeHandler.bind(this);
    this.passVariablesToLayout({'--cols': this.gridCols});
  }

  windowResizeHandler() {
    const
      screenWidth = window.innerWidth,
      screens = Object.keys(this.mapDisplayToTilesDimensions);

    if (screenWidth >= parseInt(screens[screens.length - 1], 10)) {
      this.setScreenOptions(screens[screens.length - 1]);
      return;
    }

    if (screenWidth <= parseInt(screens[0], 10)) {
      this.setScreenOptions(screens[0]);
      return;
    }

    for (let i = 1; i < screens.length; i++) {
      if (screenWidth >= parseInt(screens[i - 1], 10) && screenWidth < parseInt(screens[i], 10)) {
        this.setScreenOptions(screens[i - 1]);
        return;
      }
    }
  }

  saveSection(params) {
    this.appService.savePublicationSection(R.pick(['data', 'publicationId', 'sectionId'], params));
  }

  setScreenOptions(screen) {
    const
      {width, height, margin} = this.mapDisplayToTilesDimensions[screen];

    this.options.fixedColWidth = width;
    this.options.fixedRowHeight = height;
    this.options.margin = margin;
    this.gridService.currentScreenWidth = screen;

    // Without timeout it rises "Cannot read property 'optionsChanged' of undefined" — wait for grid to start up
    setTimeout(() => this.options.api.optionsChanged(), 0);

    this.passVariablesToLayout({
      '--item-height': height,
      '--item-width' : width,
      '--margin'     : margin,
    });
  }

  passVariablesToLayout(options) {
    const
      keys = Object.keys(options);

    for (let i = 0; i < keys.length; i++) {
      // Passing css variables through the attribute in this case does not work
      this.gridLayoutRef.nativeElement.style.setProperty(keys[i], options[keys[i]]);
    }
  }}



// WEBPACK FOOTER //
// /Users/nide/Code/3dissue/3d-issue-hubs-ui/src/app/edit/edit.component.ts